import { useState } from "react";
import { FileText } from "lucide-react";

import { api, ApiError } from "@/lib/api-client";
import { aed, CRM_INVOICE_KIND_LABEL, type CrmInvoiceKind, type OrderFull } from "@/lib/orders";
import { useLocale } from "@/lib/locale";
import { formatDate } from "@/lib/utils";
import { BTN_GHOST, Card } from "./ui";

const KINDS: CrmInvoiceKind[] = ["deposit", "balance", "full"];

/**
 * Invoices of this order in i-GlooCRM. Paid instalments push one by
 * themselves (Settings → Orders → CRM); this card lists them and lets staff
 * push one by hand — for a bank transfer invoiced before the money lands.
 */
export function CrmInvoicesCard({ order, onChanged }: { order: OrderFull; onChanged: (o: OrderFull) => void }) {
  const { locale } = useLocale();
  const cfg = order.crmInvoice;
  const [kind, setKind] = useState<CrmInvoiceKind>(order.crmInvoices.some((i) => i.kind === "deposit") ? "balance" : "deposit");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const configured = cfg?.enabled && cfg.docType && cfg.productId;
  const signed = order.status === "signed";

  async function push() {
    if (!window.confirm(`Create a ${CRM_INVOICE_KIND_LABEL[kind].toLowerCase()} invoice for ${order.number} in i-GlooCRM?`)) return;
    setBusy(true);
    setMsg(null);
    try {
      onChanged(await api.crmInvoice(order.id, kind));
      setMsg("Invoice created in the CRM.");
    } catch (e) {
      let why = "The CRM refused the invoice.";
      if (e instanceof ApiError) {
        try {
          const p = JSON.parse(e.message) as { message?: string | string[] };
          why = (Array.isArray(p.message) ? p.message.join(", ") : p.message) ?? why;
        } catch {
          why = e.message || why;
        }
      }
      setMsg(why);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card tag="Invoices · i-GlooCRM">
      {order.crmInvoices.length > 0 ? (
        <ul className="space-y-1.5 text-sm">
          {order.crmInvoices.map((i) => (
            <li key={i.id} className="flex items-center justify-between gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-2.5 py-1.5">
              <span>
                <FileText className="mr-1 inline h-3.5 w-3.5 text-goldbright" />
                <b className="text-paper">{i.invoiceNo ?? `#${i.crmInvoiceId}`}</b>
                <span className="ml-2 text-xs text-ice/60">{CRM_INVOICE_KIND_LABEL[i.kind]} · {formatDate(i.createdAt, locale)}</span>
              </span>
              <span className="num text-right">
                {aed(i.amount)}
                <span className={`ml-2 text-[0.62rem] uppercase tracking-[0.12em] ${i.paid ? "text-green" : "text-ice/50"}`}>{i.paid ? "paid" : "open"}</span>
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-ice/60">
          {configured
            ? cfg.auto
              ? "No invoice yet. One is created in the CRM as soon as an instalment is paid."
              : "No invoice yet."
            : "CRM invoices are not set up — Settings → Orders → Invoices in i-GlooCRM."}
        </p>
      )}
      {configured && (
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <label className="block grow">
            <span className="field-label">Instalment</span>
            <select className="field" value={kind} onChange={(e) => setKind(e.target.value as CrmInvoiceKind)} disabled={busy || !signed}>
              {KINDS.map((k) => (
                <option key={k} value={k} className="bg-night">{CRM_INVOICE_KIND_LABEL[k]}</option>
              ))}
            </select>
          </label>
          <button type="button" className={BTN_GHOST} disabled={busy || !signed} onClick={() => void push()} title={signed ? "" : "Only a signed order can be invoiced"}>
            <FileText className="h-3.5 w-3.5" /> {busy ? "Creating…" : "Create in CRM"}
          </button>
        </div>
      )}
      {!signed && configured && <p className="mt-2 text-xs text-ice/50">Available once the order is signed.</p>}
      {msg && <p className="mt-2 text-xs text-goldbright">{msg}</p>}
    </Card>
  );
}
